import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  doc, 
  getDoc,
  orderBy,
  limit,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../config/firebase';
import './ActivityFeed.css';

function ActivityFeed() {
  const { currentUser } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchActivities();
  }, [currentUser, filter]);

  const fetchActivities = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      let activitiesQuery;
      
      if (filter === 'friends') {
        // Get user's friends first
        const friendsQuery = query(
          collection(db, 'friendships'),
          where('users', 'array-contains', currentUser.uid),
          where('status', '==', 'accepted')
        );
        const friendsSnapshot = await getDocs(friendsQuery);
        
        const friendIds = [];
        friendsSnapshot.docs.forEach(doc => {
          const friendship = doc.data();
          const friendId = friendship.users.find(id => id !== currentUser.uid);
          friendIds.push(friendId);
        });
        
        if (friendIds.length === 0) {
          setActivities([]);
          setLoading(false);
          return;
        }
        
        // Get activities from friends
        activitiesQuery = query(
          collection(db, 'activities'),
          where('userId', 'in', friendIds),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
      } else if (filter === 'my') {
        // Get only current user's activities
        activitiesQuery = query(
          collection(db, 'activities'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
      } else {
        // Get all activities (including own and friends)
        const friendsQuery = query(
          collection(db, 'friendships'),
          where('users', 'array-contains', currentUser.uid),
          where('status', '==', 'accepted')
        );
        const friendsSnapshot = await getDocs(friendsQuery);
        
        const allUserIds = [currentUser.uid];
        friendsSnapshot.docs.forEach(doc => {
          const friendship = doc.data();
          const friendId = friendship.users.find(id => id !== currentUser.uid);
          allUserIds.push(friendId);
        });
        
        activitiesQuery = query(
          collection(db, 'activities'),
          where('userId', 'in', allUserIds),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
      }
      
      const querySnapshot = await getDocs(activitiesQuery);
      
      const activitiesData = [];
      for (const docSnap of querySnapshot.docs) {
        const activity = { id: docSnap.id, ...docSnap.data() };
        
        // Get user profile for each activity
        const userProfile = await getDoc(doc(db, 'userProfiles', activity.userId));
        if (userProfile.exists()) {
          activity.userProfile = userProfile.data();
        }
        
        activitiesData.push(activity);
      }
      
      setActivities(activitiesData);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const createActivity = async (type, data) => {
    try {
      await addDoc(collection(db, 'activities'), {
        userId: currentUser.uid,
        type: type,
        data: data,
        likes: [],
        comments: [],
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Refresh activities
      fetchActivities();
    } catch (error) {
      console.error('Error creating activity:', error);
    }
  };

  const toggleLike = async (activityId, currentLikes = []) => {
    try {
      const isLiked = currentLikes.includes(currentUser.uid);
      
      await updateDoc(doc(db, 'activities', activityId), {
        likes: isLiked 
          ? arrayRemove(currentUser.uid)
          : arrayUnion(currentUser.uid),
        updatedAt: new Date()
      });
      
      // Update local state
      setActivities(prev => 
        prev.map(activity => {
          if (activity.id === activityId) {
            const newLikes = isLiked
              ? activity.likes.filter(uid => uid !== currentUser.uid)
              : [...activity.likes, currentUser.uid];
            return { ...activity, likes: newLikes };
          }
          return activity;
        })
      );
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const addComment = async (activityId, commentText) => {
    if (!commentText.trim()) return;
    
    try {
      const comment = {
        id: Date.now().toString(),
        userId: currentUser.uid,
        text: commentText,
        createdAt: new Date()
      };
      
      await updateDoc(doc(db, 'activities', activityId), {
        comments: arrayUnion(comment),
        updatedAt: new Date()
      });
      
      // Update local state
      setActivities(prev => 
        prev.map(activity => {
          if (activity.id === activityId) {
            return { 
              ...activity, 
              comments: [...(activity.comments || []), comment]
            };
          }
          return activity;
        })
      );
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'book_added': return '📚';
      case 'book_finished': return '✅';
      case 'book_started': return '📖';
      case 'book_progress': return '📊';
      case 'friend_added': return '👥';
      case 'club_joined': return '🏛️';
      case 'club_created': return '🆕';
      case 'review_added': return '⭐';
      default: return '📱';
    }
  };

  const getActivityMessage = (activity) => {
    const { type, data } = activity;
    const userName = activity.userProfile?.displayName || 'Someone';
    
    switch (type) {
      case 'book_added':
        return `${userName} added "${data.bookTitle}" to their library`;
      case 'book_finished':
        return `${userName} finished reading "${data.bookTitle}"`;
      case 'book_started':
        return `${userName} started reading "${data.bookTitle}"`;
      case 'book_progress':
        return `${userName} updated progress on "${data.bookTitle}" (${data.progress}%)`;
      case 'friend_added':
        return `${userName} connected with ${data.friendName}`;
      case 'club_joined':
        return `${userName} joined the book club "${data.clubName}"`;
      case 'club_created':
        return `${userName} created a new book club "${data.clubName}"`;
      case 'review_added':
        return `${userName} reviewed "${data.bookTitle}" (${data.rating} stars)`;
      default:
        return `${userName} had some activity`;
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown time';
    
    const now = new Date();
    const activityDate = date.toDate ? date.toDate() : new Date(date);
    const diffInHours = (now - activityDate) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 168) {
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return activityDate.toLocaleDateString();
    }
  };

  const ActivityCard = ({ activity }) => {
    const [showComments, setShowComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentUsers, setCommentUsers] = useState({});
    
    const isLiked = activity.likes?.includes(currentUser.uid);
    const likeCount = activity.likes?.length || 0;
    const commentCount = activity.comments?.length || 0;

    useEffect(() => {
      // Fetch user profiles for comments
      const fetchCommentUsers = async () => {
        if (!activity.comments) return;
        
        const users = {};
        for (const comment of activity.comments) {
          if (!users[comment.userId]) {
            try {
              const userDoc = await getDoc(doc(db, 'userProfiles', comment.userId));
              if (userDoc.exists()) {
                users[comment.userId] = userDoc.data();
              }
            } catch (error) {
              console.error('Error fetching comment user:', error);
            }
          }
        }
        setCommentUsers(users);
      };
      
      if (showComments) {
        fetchCommentUsers();
      }
    }, [showComments, activity.comments]);

    const handleAddComment = (e) => {
      e.preventDefault();
      if (newComment.trim()) {
        addComment(activity.id, newComment);
        setNewComment('');
      }
    };

    return (
      <div className="activity-card">
        <div className="activity-header">
          <div className="activity-avatar">
            {activity.userProfile?.displayName 
              ? activity.userProfile.displayName.charAt(0).toUpperCase() 
              : 'U'}
          </div>
          <div className="activity-info">
            <div className="activity-message">
              <span className="activity-icon">{getActivityIcon(activity.type)}</span>
              {getActivityMessage(activity)}
            </div>
            <div className="activity-time">
              {formatTimeAgo(activity.createdAt)}
            </div>
          </div>
        </div>

        {activity.data?.bookCover && (
          <div className="activity-book-cover">
            <img 
              src={activity.data.bookCover} 
              alt={activity.data.bookTitle}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="activity-actions">
          <button 
            className={`activity-action ${isLiked ? 'liked' : ''}`}
            onClick={() => toggleLike(activity.id, activity.likes)}
          >
            {isLiked ? '❤️' : '🤍'} {likeCount > 0 && likeCount}
          </button>
          
          <button 
            className="activity-action"
            onClick={() => setShowComments(!showComments)}
          >
            💬 {commentCount > 0 && commentCount}
          </button>
        </div>

        {showComments && (
          <div className="activity-comments">
            {activity.comments?.map(comment => (
              <div key={comment.id} className="activity-comment">
                <div className="comment-avatar">
                  {commentUsers[comment.userId]?.displayName?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <span className="comment-author">
                      {commentUsers[comment.userId]?.displayName || 'Anonymous'}
                    </span>
                    <span className="comment-time">
                      {formatTimeAgo(comment.createdAt)}
                    </span>
                  </div>
                  <div className="comment-text">{comment.text}</div>
                </div>
              </div>
            ))}
            
            <form onSubmit={handleAddComment} className="add-comment-form">
              <input
                type="text"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="comment-input"
              />
              <button type="submit" className="comment-submit" disabled={!newComment.trim()}>
                Send
              </button>
            </form>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="activity-loading">
        <div className="loading-spinner"></div>
        <p>Loading activity feed...</p>
      </div>
    );
  }

  return (
    <div className="activity-feed-page">
      <div className="container">
        <div className="activity-header">
          <h1>📱 Activity Feed</h1>
          <p>Stay connected with your reading community</p>
        </div>

        <div className="activity-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Activity
          </button>
          <button 
            className={`filter-btn ${filter === 'friends' ? 'active' : ''}`}
            onClick={() => setFilter('friends')}
          >
            Friends Only
          </button>
          <button 
            className={`filter-btn ${filter === 'my' ? 'active' : ''}`}
            onClick={() => setFilter('my')}
          >
            My Activity
          </button>
        </div>

        <div className="activity-feed">
          {activities.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📱</div>
              <h3>No activity yet</h3>
              <p>
                {filter === 'friends' 
                  ? 'Your friends haven\'t shared any activities yet. Connect with more readers!'
                  : filter === 'my'
                  ? 'You haven\'t had any activity yet. Start reading and your activities will appear here!'
                  : 'No activities to show. Add some friends or start reading to see activities here!'
                }
              </p>
            </div>
          ) : (
            <div className="activities-list">
              {activities.map(activity => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActivityFeed;